// Single place that knows how to talk to the API.
//
// Requests are same-origin relative paths: the Vite dev proxy forwards /api
// in development and nginx does the same in production. That keeps the API
// URL out of the bundle, so the same build runs in both.

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

// Set once at sign-in and cleared at sign-out, so callers never pass a token.
let authToken: string | null = null
export function setAuthToken(token: string | null) {
  authToken = token
}

export async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
      ...init?.headers,
    },
  })

  // An expired or revoked session should return the user to sign-in rather
  // than surfacing as a puzzling failure on whatever page they were using.
  if (response.status === 401 && !path.endsWith('/auth/login')) {
    localStorage.removeItem('crm.token')
    authToken = null
    if (!window.location.pathname.startsWith('/login')) {
      window.location.assign('/login?expired=1')
    }
  }

  if (!response.ok) {
    // FastAPI puts the human-readable reason in `detail`; fall back to the
    // status text when the body is not JSON (a proxy error page, say).
    let detail = response.statusText
    try {
      const body = await response.json()
      if (typeof body?.detail === 'string') detail = body.detail
    } catch {
      /* keep statusText */
    }
    throw new ApiError(response.status, detail)
  }

  return response.json() as Promise<T>
}

export interface SystemStatus {
  status: 'ok' | 'degraded'
  database: { reachable: boolean; error?: string }
  api_port: number
}

export const getSystemStatus = () => apiFetch<SystemStatus>('/api/v1/system/status')

// --- Fax routing (DEC-015, provisional) ------------------------------------
// Data capture only. There is no send endpoint by design: transmission is out
// of scope under DEC-013.

export interface Recipient {
  slot: string
  label: string
  recipient_id: number | null
  recipient_name: string | null
  fax_stored: string | null
  fax_display: string
  include: boolean
  fax_is_placeholder: boolean
  directory_fax: string | null
  directory_fax_display: string
  drifted: boolean
  invariant_warning: string | null
}

export interface StandingDestination {
  name: string
  fax_display: string
  trigger: string
  source: string
  disputed: boolean
}

export interface FaxRouting {
  study_id: number
  patient_display: string | null
  recipients: Recipient[]
  standing_destinations: StandingDestination[]
  writable: boolean
  read_only_reason: string | null
}

export interface DirectoryEntry {
  id: number
  name: string
  fax_display: string
}

export interface RecipientInput {
  recipient_id: number | null
  fax: string | null
  include: boolean
}

export const getFaxRouting = (studyId: number) =>
  apiFetch<FaxRouting>(`/api/v1/studies/${studyId}/fax-routing`)

export const searchDirectory = (q: string) =>
  apiFetch<DirectoryEntry[]>(
    `/api/v1/studies/fax-routing/directory?q=${encodeURIComponent(q)}`,
  )

export const saveFaxRouting = (
  studyId: number,
  body: Record<string, RecipientInput>,
) =>
  apiFetch<{ saved: boolean; columns_written: string[]; message: string }>(
    `/api/v1/studies/${studyId}/fax-routing`,
    { method: 'PUT', body: JSON.stringify(body) },
  )

// --- Modality worksheets (the 14 legacy worksheet pages) --------------------
// One shape for every modality: the groups, labels and hints are data
// transcribed from the ASPX markup, so nothing here knows about a particular
// worksheet. Every column is free text in the legacy — velocities hold `---`,
// `N/A` and `>317` — so values are strings both ways.

export interface WorksheetField {
  label: string
  column: string
  hint: string
  value: string | null
  wide: boolean
  // 'text', 'checkbox' or 'select' — how the legacy drew the control.
  kind?: string
  // The options for a 'select', verbatim from the legacy markup.
  choices?: string[]
  // Standard sentences a 'text' field can have *appended* to it, rather than a
  // value that replaces what is there — the echo/TEE Add-comment buttons.
  phrases?: string[]
}

// `BuildComments` (`EchocardiographyReport.aspx.vb:128`): the existing text, two
// spaces, the new phrase, trimmed. Kept identical to `worksheets.append_phrase`
// on the server — it is the separator every stored comment already uses.
export const appendPhrase = (existing: string, phrase: string) =>
  `${existing}  ${phrase}`.trim()

export interface WorksheetGroup {
  title: string
  columns: number
  fields: WorksheetField[]
}

export interface Worksheet {
  study_id: number
  modality: string
  title: string
  legacy_page: string
  table: string
  exists: boolean
  groups: WorksheetGroup[]
}

export interface WorksheetChange {
  from: string | number | null
  to: string | number | null
}

export interface WorksheetSaved {
  changed: Record<string, WorksheetChange>
  saved: boolean
  created: boolean
}

export const getWorksheet = (studyId: number, modality: string) =>
  apiFetch<Worksheet>(`/api/v1/studies/${studyId}/worksheet/${modality}`)

// Keyed by legacy column name, and partial by design: only the fields the user
// actually changed are sent, so a visit that touches nothing rewrites nothing.
// That is the one thing the legacy worksheet gets wrong — it saves on page load
// and rewrites every column.
export const saveWorksheet = (
  studyId: number,
  modality: string,
  patch: Record<string, string | null>,
) =>
  apiFetch<WorksheetSaved>(`/api/v1/studies/${studyId}/worksheet/${modality}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  })

// --- Study assignment (StudyInformation.aspx `SaveStudy`) -------------------

export interface AssignmentOption {
  id: number
  name: string
}

export interface AssignmentValues {
  reading_physician_id: number | null
  reviewing_physician_id: number | null
  technician_id: number | null
  study_type_id: number | null
  study_facility_id: number | null
  reading_facility_id: number | null
  study_room: string | null
  patient_height: string | null
  patient_weight: string | null
  patient_sex: string | null
  patient_comments: string | null
}

export interface AssignmentOptions {
  reading_physicians: AssignmentOption[]
  technicians: AssignmentOption[]
  study_types: AssignmentOption[]
  facilities: AssignmentOption[]
  statuses: AssignmentOption[]
}

export interface Assignment {
  study_id: number
  status_id: number | null
  values: AssignmentValues
  options: AssignmentOptions
  will_move_to_pending: boolean
}

export interface AssignmentChange {
  from: string | number | null
  to: string | number | null
}

export interface AssignmentSaved {
  study_id: number
  changed: Record<string, AssignmentChange>
  status_before: number | null
  status_after: number | null
  status_reason: string | null
  saved: boolean
  next: string
}

// `readingFacilityId` re-scopes the reading physician list the way
// `ddlReadingFacility_SelectedIndexChanged` rebinds it. Zero (the "Select a
// Facility" entry) is left off: the server reads it as "no facility chosen".
export const getAssignment = (studyId: number, readingFacilityId?: number) =>
  apiFetch<Assignment>(
    `/api/v1/studies/${studyId}/assignment` +
      (readingFacilityId ? `?readingFacilityId=${readingFacilityId}` : ''),
  )

// Partial by design: only the fields the user actually changed are sent, so a
// visit that touches nothing rewrites nothing.
export const saveAssignment = (
  studyId: number,
  patch: Partial<Record<keyof AssignmentValues, string | number | null>>,
) =>
  apiFetch<AssignmentSaved>(`/api/v1/studies/${studyId}/assignment`, {
    method: 'POST',
    body: JSON.stringify(patch),
  })

// --- Study Report hub actions (StudyReport.aspx `btnESign`, `btnComplete`,
// `cmdRefreshReport`) ---------------------------------------------------------
// Send Report and Delete are deliberately absent: transmission is out of scope
// (DEC-013) and study deletion is not reproduced (DEC-017).

// Which buttons the legacy would draw for this user and this study. The
// endpoints enforce the same answers, so this is only so a control can be shown
// disabled with a reason rather than 403-ing when pressed.
export interface ReportActions {
  study_id: number
  study_type_id: number | null
  study_type: string | null
  status_id: number | null
  status: string | null
  can_esign: boolean
  esign_reason: string | null
  esign_table: string | null
  esign_page: string | null
  // False only for a reading physician signing an Echo/TEE/Carotid read they
  // are linked to — the one case `ElectronicSignature.aspx` skips the PIN.
  esign_needs_pin: boolean
  already_signed: boolean
  can_mark_completed: boolean
  mark_completed_reason: string | null
  already_completed: boolean
  refresh_available: boolean
  refresh_reason: string | null
}

export interface ActionChange {
  from?: string | number | boolean | null
  to?: string | number | boolean | null
  stored?: string | number | boolean | null
  live?: string | number | boolean | null
}

export interface ActionResult {
  study_id: number
  saved: boolean
  changed: Record<string, ActionChange>
  status_before: number | null
  status_after: number | null
  note: string | null
  table: string | null
  // E-Sign only.
  physician?: string | null
  legacy_page?: string | null
  completed?: boolean
  // Refresh only.
  crystal_export?: boolean
  drift?: Record<string, ActionChange> | null
  referenced_file?: string | null
}

export const getReportActions = (studyId: number) =>
  apiFetch<ReportActions>(`/api/v1/studies/${studyId}/report/actions`)

export const esignReport = (studyId: number, pin: string | null) =>
  apiFetch<ActionResult>(`/api/v1/studies/${studyId}/report/esign`, {
    method: 'POST',
    body: JSON.stringify({ pin }),
  })

export const completeStudy = (studyId: number) =>
  apiFetch<ActionResult>(`/api/v1/studies/${studyId}/report/complete`, {
    method: 'POST',
  })

export const refreshReport = (studyId: number) =>
  apiFetch<ActionResult>(`/api/v1/studies/${studyId}/report/refresh`, {
    method: 'POST',
  })

// --- The worklist's Billed command (MainStudyList.aspx.vb:582) -------------
// One row in `Charges`, once per study. The legacy inserts unconditionally and
// 1,056 studies carry a duplicate as a result; the server refuses the repeat
// and says so, so `saved` is false rather than an error when a study is already
// billed. Gated on permission 2 server-side, which is the same permission that
// decides whether the legacy grid draws the column.

export interface BilledResult {
  study_id: number
  saved: boolean
  already_billed: boolean
  charges_before: number
  charges_after: number
  note: string | null
  table: string
}

export const markBilled = (studyId: number) =>
  apiFetch<BilledResult>(`/api/v1/studies/${studyId}/billed`, { method: 'POST' })

// --- Stored report PDF (`ReportViewer.aspx`) --------------------------------
// The legacy viewer took the filename from the query string and concatenated it
// onto `g:\Reports\`. Here the name comes from the study's report row and the
// request only carries the study id, so there is nothing to point elsewhere.

export interface StoredPdfInfo {
  study_id: number
  study_type: string | null
  table: string
  key: string
  key_value: number | null
  referenced_file: string | null
  has_stored_pdf: boolean
  /** False when the reports share is unconfigured or unmounted. */
  share_available: boolean
  unavailable_reason: string | null
}

export const getStoredPdfInfo = (studyId: number) =>
  apiFetch<StoredPdfInfo>(`/api/v1/studies/${studyId}/report/pdf/info`)

// The file itself. `apiFetch` parses JSON, so this one is written out: the
// response is a PDF on success and a FastAPI `detail` object on failure, and
// the failure message is the useful half — "the share is not mounted" is a
// different problem from "this report was never generated".
export async function fetchStoredPdf(studyId: number): Promise<string> {
  const path = `/api/v1/studies/${studyId}/report/pdf`
  const response = await fetch(path, {
    headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
  })
  if (!response.ok) {
    let detail = response.statusText
    try {
      const body = await response.json()
      if (typeof body?.detail === 'string') detail = body.detail
    } catch {
      /* keep statusText */
    }
    throw new ApiError(response.status, detail)
  }
  return URL.createObjectURL(await response.blob())
}

// A PDF rendered on demand from current data, rather than the file the legacy
// exported at save time. Returns the blob URL and the legacy filename, which
// the server puts in Content-Disposition — the name matters, because it is what
// the file is called on the share and how anyone looking for it will search.
export async function generateReportPdf(
  studyId: number,
): Promise<{ url: string; filename: string }> {
  const response = await fetch(`/api/v1/studies/${studyId}/report/pdf/generate`, {
    headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
  })
  if (!response.ok) {
    let detail = response.statusText
    try {
      const body = await response.json()
      if (typeof body?.detail === 'string') detail = body.detail
    } catch {
      /* keep statusText */
    }
    throw new ApiError(response.status, detail)
  }
  const disposition = response.headers.get('Content-Disposition') ?? ''
  const match = /filename\*=UTF-8''([^;]+)|filename="([^"]*)"/.exec(disposition)
  const filename = match
    ? decodeURIComponent(match[1] ?? match[2] ?? '')
    : `study-${studyId}.pdf`
  return { url: URL.createObjectURL(await response.blob()), filename }
}

// The activity and transcription reports as a printed document.
//
// `ActivityReportPDF.aspx` and `ActivityReportPDFTranscription.aspx` were Crystal
// hosts: what the user took away from those screens was always the document, and
// the rewrite drew the grid without ever reproducing it. `query` is the same
// filter string the grid is showing, so the printed report is what is on screen.
export async function fetchActivityPdf(
  path: 'activity' | 'activity/transcription',
  query: string,
): Promise<{ url: string; filename: string }> {
  const response = await fetch(`/api/v1/${path}/pdf?${query}`, {
    headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
  })
  if (!response.ok) {
    let detail = response.statusText
    try {
      const body = await response.json()
      if (typeof body?.detail === 'string') detail = body.detail
    } catch {
      /* keep statusText */
    }
    throw new ApiError(response.status, detail)
  }
  const disposition = response.headers.get('Content-Disposition') ?? ''
  const match = /filename="([^"]*)"/.exec(disposition)
  return {
    url: URL.createObjectURL(await response.blob()),
    filename: match?.[1] ?? 'report.pdf',
  }
}

// Report artwork — logos, anatomical diagrams, the arrows labelling them, and
// physician signature graphics.
//
// Fetched rather than put straight in an `<img src>`, because the API
// authenticates with a bearer token and a browser does not send headers with an
// image request: `<img src="/api/v1/reports/assets/…">` is an unauthenticated
// GET and comes back 401, so every picture on every report would be a broken
// image. Leaving the endpoint open instead was the alternative and is the wrong
// one — signature graphics are on it, and an unauthenticated URL is how the
// legacy's ReportViewer leaked every report in the system.
//
// Cached by path for the session. The assets are immutable for the life of a
// build, a report can reference seventeen of them, and the same logo appears on
// every report a user opens.
const assetCache = new Map<string, Promise<string>>()

export function reportAssetUrl(path: string): Promise<string> {
  let pending = assetCache.get(path)
  if (!pending) {
    pending = fetch(path, {
      headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
    })
      .then((r) => {
        if (!r.ok) throw new ApiError(r.status, `asset ${path}: ${r.statusText}`)
        return r.blob()
      })
      .then(URL.createObjectURL)
      .catch((e) => {
        // A failed fetch must not be cached, or one blip blanks the picture for
        // the rest of the session.
        assetCache.delete(path)
        throw e
      })
    assetCache.set(path, pending)
  }
  return pending
}

// --- Image inventory (`VerifyLocation.aspx`) --------------------------------

export interface StudySeries {
  series_id: number
  series_instance_uid: string
  modality: string | null
  series_number: number | null
  /** `Series.ImageCollection.Count` — the number the legacy prints. */
  // `null` when the cross-study count was not asked for. Counting a series
  // across every study scans a 118-million-row table once per series — 67
  // seconds for a study with 111 of them — because no index leads on
  // `SeriesInstanceUID`. Request it with `crossStudyCounts`.
  image_count: number | null
  image_count_in_study: number
  /** In `Images` for this study but with no `Series` row. */
  orphan: boolean
}

export interface StudyImages {
  study_id: number
  study_instance_uid: string
  series: StudySeries[]
  series_count: number
  /** `mStudy.ImageCollection.Count`, the number `BindPage` writes first. */
  study_image_count: number
  series_image_total: number
  orphan_series_count: number
  orphan_image_count: number
  elapsed_ms: number
  notes: string[]
  update_location: string
  view_online: string
}

export const getStudyImages = (studyId: number, crossStudyCounts = false) =>
  apiFetch<StudyImages>(
    `/api/v1/studies/${studyId}/images${crossStudyCounts ? '?crossStudyCounts=true' : ''}`,
  )

// --- Structured interpretation forms (echo / TEE / carotid) ----------------
// `EchocardiographyInterpretationForm.aspx`, `TeeInterpretationForm.aspx`,
// `CarotidInterpretationForm.aspx`. The layout — sections, labels, tooltips,
// which controls the legacy actually persists — is served as data, so nothing
// about a particular form is known on this side.

export interface InterpretationItem {
  kind: 'check' | 'text' | 'select'
  name: string
  label: string
  tooltip: string
  /** null where the legacy renders the control but never stores it. */
  column: string | null
  /** Companion free-text control appended to the built sentence (TEE). */
  textbox: string | null
  choices: string[] | null
  rows: number | null
  value: boolean | string
}

export interface InterpretationSection {
  title: string
  note: string
  items: InterpretationItem[]
}

export interface InterpretationForm {
  study_id: number
  study_type_id: number | null
  study_type: string | null
  status_id: number | null
  form: string
  title: string
  legacy_page: string
  table: string | null
  report_id: number | null
  interpretation_id: number | null
  note: string
  /** TEE: saving discards the typed text and rebuilds it from the ticks. */
  rebuilds_on_save: boolean
  dictated_not_read: boolean
  interpretation: string
  /** What Build would produce from the values as stored. */
  built: string
  sections: InterpretationSection[]
}

export interface InterpretationSaved {
  study_id: number
  form: string
  report_id: number
  interpretation_id: number | null
  created_interpretation: boolean
  changed: Record<string, unknown>
  interpretation: string
  status_before: number | null
  status_after: number | null
  status_reason: string | null
  saved: boolean
}

export const getInterpretation = (studyId: number, form: string) =>
  apiFetch<InterpretationForm>(`/api/v1/studies/${studyId}/interpretation/${form}`)

// The Build button. Composed on the server so the separators, the ordering and
// TEE's always-printed headings have one implementation, the one Save uses.
export const buildInterpretation = (
  studyId: number,
  form: string,
  body: { checks: Record<string, boolean>; texts: Record<string, string> },
) =>
  apiFetch<{ interpretation: string }>(
    `/api/v1/studies/${studyId}/interpretation/${form}/build`,
    { method: 'POST', body: JSON.stringify(body) },
  )

// The whole form, as the legacy postback would carry it: a checkbox missing
// from `checks` is unticked.
export const saveInterpretation = (
  studyId: number,
  form: string,
  body: {
    checks?: Record<string, boolean>
    texts?: Record<string, string>
    selects?: Record<string, string>
    interpretation?: string | null
    dictated_not_read?: boolean
  },
) =>
  apiFetch<InterpretationSaved>(`/api/v1/studies/${studyId}/interpretation/${form}`, {
    method: 'POST',
    body: JSON.stringify(body),
  })

// There is no study-deletion call here, and there is no route behind one.
// `MainStudyList.aspx.vb:567` has the command; it is not reproduced (DEC-017).
